import { Component, OnInit, HostListener, TemplateRef, ViewChild } from '@angular/core';
import { FeFlowNetworkService } from '../../services/fe-flow-network.service';
import { ToastrService } from 'ngx-toastr';
import { ErrorHandler } from '../../utils/error-handler';
import { MatDialog } from '@angular/material/dialog';
import { exportRowsToExcel } from '../../utils/excel-export';
import { AuthService } from '../../services/auth.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-fe-flow-network',
  templateUrl: './flow-network.component.html',
  styleUrls: ['./flow-network.component.scss'],
  standalone: false,
})
export class FeFlowNetworkComponent implements OnInit {
  private readonly blankFilterToken = 'NULL';

  @ViewChild('exportOptionsDialog') exportOptionsDialog!: TemplateRef<any>;
  @ViewChild('bulkUpdateDialog') bulkUpdateDialog!: TemplateRef<any>;

  constructor(
    private flowNetworkService: FeFlowNetworkService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private authService: AuthService
  ) {}

  data: any[] = [];
  lastRowNumber: number = 0;
  spinnerBoolean: boolean = false;
  isLoading: boolean = false;
  allRowsLoaded: boolean = false;
  recordNotFound: boolean = false;
  columnFilterOptions: {
    [key: string]: {
      loading: boolean;
      options: string[];
    };
  } = {};

  hoveredRowIndex: number | null = null;
  filterSearch: { [key: string]: string } = {};
  multiFilters: { [key: string]: string[] } = {};
  activeFilter: string = '';
  showFloatingFilter: boolean = false;
  dropdownPosition: { top: number; left: number } = { top: 0, left: 0 };
  trainLookupPerRow: { [key: string]: string[] } = {};
  fieldNameMap: { [key: string]: string } = {
    wellName: 'WellName',
    wellPropId: 'WellPropID',
    pad: 'PadName',
    devArea: 'DevelopmentArea',
    sat: 'SAT',
    ctb: 'CTB',
    ctbTrain: 'CTBTrain',
    cs: 'CS',
    swd: 'SWD',
  };
  backendFilters: any = {
    WellName: null,
    WellPropID: null,
    PadName: null,
    DevelopmentArea: null,
    CTB: null,
    CTBTrain: null,
    CS: null,
    SWD: null,
    SAT: null,
  };

  columns = [
    { field: 'wellName', header: 'Well', editable: false, filterable: true },
    {
      field: 'wellPropId',
      header: 'WellPropID',
      editable: false,
      filterable: true,
    },
    { field: 'devArea', header: 'Dev Area', editable: false, filterable: true },
    { field: 'pad', header: 'Pad', editable: false, filterable: true },
    {
      field: 'sat',
      header: 'SAT',
      editable: true,
      lookup: true,
      filterable: true,
    },
    {
      field: 'ctbTrain',
      header: 'Train',
      editable: true,
      lookup: true,
      filterable: true,
    },
    {
      field: 'ctb',
      header: 'CTB',
      editable: true,
      lookup: true,
      filterable: true,
    },
    {
      field: 'cs',
      header: 'CS',
      editable: true,
      lookup: true,
      filterable: true,
    },
    {
      field: 'swd',
      header: 'SWD',
      editable: true,
      lookup: true,
      filterable: true,
    },
  ];

  lookupCache: { [devArea: string]: any } = {};

  corridorDetailsCache: { [csValue: string]: any } = {};
  corridorFacilityNames: { [csValue: string]: string[] } = {};
  corridorDetailsLoading: { [csValue: string]: boolean } = {};
  hoveredCsValue: string | null = null;
  bulkUpdatePadName: string = '';
  bulkUpdatePadNames: string[] = [];
  bulkUpdateEditedRowCount: number = 0;

  private readonly editableFilterSnapshotFields = ['sat', 'ctbTrain', 'ctb', 'cs', 'swd'];

  private isBlankFilterValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    const normalized = String(value).trim();
    return normalized === '' || normalized.toLowerCase() === 'null';
  }

  private normalizeFilterValue(value: unknown): string {
    if (this.isBlankFilterValue(value)) {
      return this.blankFilterToken;
    }

    return String(value).trim();
  }

  private normalizeFilterOptions(values: unknown[] | undefined): string[] {
    if (!Array.isArray(values)) {
      return [];
    }

    const normalizedValues = Array.from(
      new Set(values.map((value) => this.normalizeFilterValue(value)))
    );

    const blankIndex = normalizedValues.indexOf(this.blankFilterToken);
    if (blankIndex > 0) {
      normalizedValues.splice(blankIndex, 1);
      normalizedValues.unshift(this.blankFilterToken);
    }

    return normalizedValues;
  }

  trackByFacilityName(index: number, facilityName: string): string {
    return facilityName;
  }

  private extractCorridorFacilityNames(resp: any): string[] {
    if (!resp || !Array.isArray(resp.feCorridorNames)) return [];

    const rawNames: string[] = [];

    for (const entry of resp.feCorridorNames) {
      const val = entry?.facilityNames;
      if (typeof val === 'string') {
        val
          .split(/[,;|]/)
          .map((s) => s.trim())
          .filter((s) => !!s)
          .forEach((s) => rawNames.push(s));
      }
    }

    return Array.from(new Set(rawNames));
  }

  isCorridorLoading(csValue: string): boolean {
    return !!this.corridorDetailsLoading[csValue];
  }

  getFacilityNamesFor(csValue: string): string[] {
    return this.corridorFacilityNames[csValue] || [];
  }

  getCorridorTitle(row: any): string | null {
    const cs = (row?.cs || '').trim();
    if (!cs) return null;

    if (this.isCorridorLoading(cs)) {
      return 'Loading corridor details...';
    }

    const names = this.getFacilityNamesFor(cs);

    if (this.corridorDetailsCache.hasOwnProperty(cs)) {
      return names.length
        ? `Combined Facilties: {${names.join(', ')}}`
        : 'Not Combined Facility';
    }

    return 'Hover to load corridor details';
  }

  getColumnTooltip(row: any, col: any): string {
    if (col.field === 'cs') {
      return this.getCorridorTitle(row) || '';
    }

    const value = row[col.field];
    if (!value || value.toString().length < 20) {
      return '';
    }

    return value.toString();
  }

  clearCorridorCache(): void {
    this.corridorDetailsCache = {};
    this.corridorFacilityNames = {};
    this.corridorDetailsLoading = {};
  }

  async loadLookupsForRow(row: any): Promise<void> {
    const devArea = row.devArea;

    if (!devArea || this.lookupCache[devArea]) {
      return;
    }

    try {
      const res = await this.flowNetworkService.getFacilitiesByDevArea({
        devArea,
      });
      this.lookupCache[devArea] = res;
    } catch (err) {
      this.lookupCache[devArea] = {
        ctb: [],
        cs: [],
        swd: [],
        satellite: [],
      };
    }
  }
  getDynamicLookupOptions(row: any, field: string): string[] {
    const devArea = row.devArea;
    const cache = this.lookupCache[devArea];

    const currentValue = row[field];
    const fallback = currentValue ? [currentValue] : [];

    if (!cache) return fallback;

    const map: { [key: string]: string[] } = {
      ctb: cache.ctb || [],
      cs: cache.cs || [],
      swd: cache.swd || [],
      sat: cache.satellite || [],
    };

    const options = map[field] || [];

    if (currentValue && !options.includes(currentValue)) {
      return [currentValue, ...options];
    }

    return options;
  }

  get filteredData() {
    return this.data.filter((row) => {
      for (const field in this.multiFilters) {
        const selected = this.multiFilters[field];
        const rowValue = this.normalizeFilterValue(row[field]);
        if (
          selected?.length &&
          !selected.includes(rowValue) &&
          !this.rowMatchesSnapshotFilter(row, field, selected)
        ) {
          return false;
        }
      }
      return true;
    });
  }

  beginRowEdit(row: any): void {
    if (row.isEdited || row.editFilterSnapshot) {
      return;
    }

    row.editFilterSnapshot = this.editableFilterSnapshotFields.reduce(
      (snapshot: any, field: string) => {
        snapshot[field] = row[field];
        return snapshot;
      },
      {}
    );
  }

  private rowMatchesSnapshotFilter(
    row: any,
    field: string,
    selected: string[]
  ): boolean {
    const snapshot = row.editFilterSnapshot;

    if (!row.isEdited || !snapshot || !selected?.length) {
      return false;
    }

    return selected.includes(this.normalizeFilterValue(snapshot[field]));
  }

  ngOnInit(): void {
    this.loadBackendFilterOptions();
    this.loadInitialData();
  }

  async loadInitialData(checkEmpty: boolean = false): Promise<void> {
    this.spinnerBoolean = true;
    try {
      const { rows, nextRowNumber } =
        await this.flowNetworkService.getFlowNetworkDataBackend({
          ...this.backendFilters,
          lastRowNumber: 0,
        });

      const mappedRows = this.mapResponse(rows);
      this.data = mappedRows;
      this.lastRowNumber = nextRowNumber;
      this.allRowsLoaded = false;

      if (checkEmpty && mappedRows.length === 0) {
        this.data = [];
        this.recordNotFound = true;
        setTimeout(() => {
          this.recordNotFound = false;
          this.backendFilters['WellPropID'] = null;
          this.filterSearch['wellPropId'] = '';
          this.multiFilters['wellPropId'] = [];

          this.loadInitialData();
          this.loadBackendFilterOptions();
        }, 3000);
      } else {
        this.recordNotFound = false;
      }
    } catch (error) {
      this.recordNotFound = false;
    } finally {
      this.spinnerBoolean = false;
    }
  }

  async loadBackendFilterOptions(): Promise<void> {
    for (const field in this.fieldNameMap) {
      const backendField = this.fieldNameMap[field] || field;
      this.columnFilterOptions[backendField] = {
        loading: true,
        options: [],
      };
    }

    try {
      const response = await this.flowNetworkService.getFilterDropdownOptions(
        this.backendFilters
      );
      const updatedFields: Set<string> = new Set();

      for (const item of response) {
        if (item.columnName && Array.isArray(item.values)) {
          this.columnFilterOptions[item.columnName] = {
            loading: false,
            options: this.normalizeFilterOptions(item.values),
          };
          updatedFields.add(item.columnName);
        }
      }
      for (const field in this.columnFilterOptions) {
        if (!updatedFields.has(field)) {
          this.columnFilterOptions[field].loading = false;
          this.columnFilterOptions[field].options = [];
        }
      }
    } catch (err) {

      for (const field in this.columnFilterOptions) {
        this.columnFilterOptions[field].loading = false;
        this.columnFilterOptions[field].options = [];
      }
    }
  }

  async onScroll(index: number): Promise<void> {
    if (this.isLoading || this.allRowsLoaded || index < this.data.length - 20)
      return;

    this.isLoading = true;

    try {
      const { rows, nextRowNumber } =
        await this.flowNetworkService.getFlowNetworkDataBackend({
          ...this.backendFilters,
          lastRowNumber: this.lastRowNumber,
        });

      const mappedRows = this.mapResponse(rows);

      if (!rows.length && this.lastRowNumber !== 0) {
        this.allRowsLoaded = true;
      } else {
        this.data.push(...mappedRows);
        this.lastRowNumber = nextRowNumber;
        if (nextRowNumber === null || nextRowNumber === undefined) {
          this.allRowsLoaded = true;
        }
      }
    } catch (error) {
    } finally {
      this.isLoading = false;
    }
  }
  getFilterOptions(field: string): string[] {
    const backendField = this.fieldNameMap[field] || field;
    const entry = this.columnFilterOptions[backendField];

    if (!entry) return [];
    return entry.options || [];
  }

  isLoadingOptions(field: string): boolean {
    const backendField = this.fieldNameMap[field] || field;
    return this.columnFilterOptions[backendField]?.loading || false;
  }

  onLookupChange(row: any, field: string): void {
    this.markRowAsEdited(row);

    if (field === 'ctb') {
      row.ctbTrain = '';
      const rowId = row.wellPropId;
      delete this.trainLookupPerRow[rowId];
    }
  }

  private mapResponse(raw: any[]): any[] {
    return raw.map((item: any) => ({
      wellName: item.wellName || '',
      wellPropId: item.wellPropID || '',
      devArea: item.developmentAreaName || '',
      pad: item.padName || '',
      sat: item.sat ?? '',
      ctb: item.ctb ?? '',
      ctbTrain: item.ctbTrain ?? '',
      cs: item.cs ?? '',
      swd: item.swd ?? '',
    }));
  }

  async onTrainLookupFocus(row: any): Promise<void> {
    const ctbValue = row.ctb;
    const rowId = row.wellPropId;

    if (!ctbValue || this.trainLookupPerRow[rowId]) return;

    try {
      const res = await this.flowNetworkService.getTrainValuesByFacilityName({
        facilityName: ctbValue,
      });
      const trainValues = (res?.train || []).map((t: any) => String(t));
      this.trainLookupPerRow[rowId] = trainValues;
    } catch (error) {
      this.trainLookupPerRow[rowId] = [];
    }
  }

  getTrainOptions(row: any): string[] {
    const rowId = row.wellPropId;
    const cached = this.trainLookupPerRow[rowId] || [];
    const currentValue = row['ctbTrain'];

    if (currentValue && !cached.includes(currentValue)) {
      return [currentValue, ...cached];
    }

    return cached;
  }

  openFloatingFilter(event: MouseEvent, field: string): void {
    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.dropdownPosition = {
      top: rect.bottom - 130+ window.scrollY,
      left: rect.left - 90 + window.scrollX,
    };
    this.activeFilter = field;
    this.showFloatingFilter = true;
  }

  handleTrainFocus(row: any): void {
    this.beginRowEdit(row);
    void this.onTrainLookupFocus(row);
  }

  handleLookupFocus(row: any): void {
    this.beginRowEdit(row);
    void this.loadLookupsForRow(row);
  }

  onTrainChange(row: any): void {
    this.markRowAsEdited(row);
  }

  isChecked(field: string, value: string): boolean {
    return this.multiFilters[field]?.includes(value);
  }

  toggleMultiFilter(field: string, value: string, event: any): void {
    const isChecked = event.target.checked;
    if (!this.multiFilters[field]) this.multiFilters[field] = [];
    if (isChecked) {
      if (!this.multiFilters[field].includes(value)) {
        this.multiFilters[field].push(value);
      }
    } else {
      this.multiFilters[field] = this.multiFilters[field].filter(
        (v) => v !== value
      );
    }
  }

  isFilterApplied(field: string): boolean {
    const search = this.filterSearch[field]?.trim();
    const multi = this.multiFilters[field]?.length;
    return !!(search || multi);
  }

  applyFilter(field: string): void {
    const backendField = this.fieldNameMap[field] || field;
    if (field === 'wellPropId') {
      const value = this.filterSearch[field]?.trim();
      if (value) {
        this.backendFilters[backendField] = value;
        this.showFloatingFilter = false;
        this.activeFilter = '';
        this.loadInitialData(true);
        return;
      } else {
        this.recordNotFound = false;
      }
    }
    this.backendFilters[backendField] = this.multiFilters[field]?.length
      ? this.multiFilters[field]
          .map((value) => this.normalizeFilterValue(value))
          .join(',')
      : null;
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadBackendFilterOptions();
    this.loadInitialData();
  }

  clearFilter(field: string): void {
    const backendField = this.fieldNameMap[field] || field;
    this.multiFilters[field] = [];
    this.filterSearch[field] = '';
    this.backendFilters[backendField] = null;
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.recordNotFound = false;
    this.loadBackendFilterOptions();
    this.loadInitialData();
  }

  hasAnyActiveFilters(): boolean {
    return (
      Object.values(this.filterSearch).some((v) => !!v?.trim()) ||
      Object.values(this.multiFilters).some((v) => Array.isArray(v) && v.length)
    );
  }

  private resetAllFiltersState(): void {
    for (const field in this.multiFilters) {
      this.multiFilters[field] = [];
    }
    for (const field in this.filterSearch) {
      this.filterSearch[field] = '';
    }
    for (const key in this.backendFilters) {
      this.backendFilters[key] = null;
    }

    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.recordNotFound = false;
  }

  clearAllFilters(): void {
    this.resetAllFiltersState();
    this.loadBackendFilterOptions();
    this.loadInitialData();
  }

  @HostListener('document:click', ['$event'])
  onOutsideClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedInsideDropdown =
      target.closest('.floating-filter-dropdown') ||
      target.closest('.filter-icon');

    if (!clickedInsideDropdown) {
      this.showFloatingFilter = false;
      this.activeFilter = '';
    }
  }

  trackByRowId(index: number, row: any): string | number {
    return row?.wellPropId ?? index;
  }

  async saveRows(): Promise<void> {
    const editedRows = this.data.filter((row) => row.isEdited);
    if (!editedRows.length) {
      this.toastr.info('No changes to save.', 'Info');
      return;
    }

    const userEmail = this.authService.getUserEmail();
    const bulkUpdate = await this.getBulkUpdateValue(editedRows);
    this.spinnerBoolean = true;
    let successCount = 0;
    let failureCount = 0;
    const errorMessages: string[] = [];

    for (const row of editedRows) {
      const body = {
        name: userEmail,
        propID: row.wellPropId,
        devArea: row.devArea,
        padName: row.pad,
        ctb: row.ctb?.trim() || null,
        ctbTrain: row.ctbTrain?.trim() || null,
        cs: row.cs?.trim() || null,
        swd: row.swd?.trim() || null,
        sat: row.sat?.trim() || null,
        bulkUpdate,
      };

      const response = await this.flowNetworkService.updateFlowNetworkFE(body);
      const parsed = ErrorHandler.parseApiResult(response);

      if (parsed.success) {
        successCount++;
        row.isEdited = false;
        delete row.editFilterSnapshot;
      } else {
        failureCount++;
        errorMessages.push(parsed.message || 'Failed to save row.');
      }
    }

    this.spinnerBoolean = false;

    if (successCount > 0) {
      this.toastr.success(
        `${successCount} row(s) saved successfully.`,
        'Success'
      );
    }
    if (failureCount > 0) {
      const detailedErrorMsg =
        errorMessages.length > 0
          ? errorMessages.join('<br>')
          : `${failureCount} row(s) failed to save.`;

      this.toastr.error(detailedErrorMsg, 'Error', {
        timeOut: 8000,
        enableHtml: true,
      });
    }
    if (successCount === 0 && failureCount === 0) {
      this.toastr.info('No rows were saved.', 'Info');
    }

    this.resetAllFiltersState();
    this.loadBackendFilterOptions();
    await this.loadInitialData();
  }

  private async getBulkUpdateValue(rows: any[]): Promise<number> {
    const padNames = Array.from(
      new Set(rows.map((row) => row.pad?.trim()).filter((padName) => !!padName))
    );

    if (!padNames.length) {
      return 0;
    }

    this.bulkUpdateEditedRowCount = rows.length;
    this.bulkUpdatePadNames = padNames;
    this.bulkUpdatePadName = padNames[0];

    const dialogRef = this.dialog.open(this.bulkUpdateDialog, {
      width: '520px',
      maxWidth: '92vw',
      disableClose: false,
      autoFocus: false,
      restoreFocus: false,
      panelClass: 'flow-network-bulk-update-dialog',
    });

    const shouldBulkUpdate = await firstValueFrom(dialogRef.afterClosed());
    this.bulkUpdateEditedRowCount = 0;
    this.bulkUpdatePadNames = [];
    this.bulkUpdatePadName = '';

    return shouldBulkUpdate ? 1 : 0;
  }

  markRowAsEdited(row: any): void {
    this.beginRowEdit(row);
    row.isEdited = true;
  }

  trackByFilterOption(index: number, option: string): string {
    return option;
  }

  getFilterOptionLabel(option: string): string {
    return this.isBlankFilterValue(option) ? '(Blank)' : option;
  }

  getFilteredDropdownOptions(field: string): string[] {
    const search = this.filterSearch[field]?.trim().toLowerCase() || '';
    const options = this.getFilterOptions(field);
    if (!search) return options;
    return options.filter((opt) =>
      this.getFilterOptionLabel(opt).toLowerCase().includes(search)
    );
  }

  async exportCSV() {
    if (!this.data.length) {
      this.toastr.info('No data available to export.');
      return;
    }

    const worksheetData = this.data.map((row) => {
      const exportRow: Record<string, any> = {};

      this.columns.forEach((column) => {
        const value = row[column.field];
        exportRow[column.header] = Array.isArray(value) ? value.join(', ') : value ?? '';
      });

      return exportRow;
    });

    await exportRowsToExcel(
      worksheetData,
      this.columns.map((column) => column.header),
      'fe-flow-network.xlsx',
      'FEFlowNetwork'
    );
  }

  openExportDialog(): void {
    this.dialog.open(this.exportOptionsDialog, {
      width: '380px',
      disableClose: true,
    });
  }

  async downloadAllData(): Promise<void> {
    try {
      const { blob, fileName } = await this.flowNetworkService.exportAllFlowNetworkData();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      this.toastr.success('Export downloaded successfully.');
    } catch (error: any) {
      this.toastr.error(error?.message || 'Failed to download export.');
    }
  }
}
